import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Leaf, CalendarCheck, CreditCard, Users, Satellite } from "lucide-react";
import { useAuth } from "@/lib/auth";

const services = [
  {
    icon: Satellite,
    title: "Instant lawn quotes",
    body: "We measure the property from satellite imagery and price the job before anyone drives out.",
  },
  {
    icon: CalendarCheck,
    title: "Scheduling that fills itself",
    body: "Weekend windows, recurring plans and route-ready stop lists for every crew.",
  },
  {
    icon: MapPin,
    title: "Live crew tracking",
    body: "GPS on every mower so homeowners know exactly when their yard gets cut.",
  },
  {
    icon: CreditCard,
    title: "Payments handled",
    body: "Cards are saved up front and charged when the work is finished. No invoices to chase.",
  },
  {
    icon: Users,
    title: "Crews and owners in one place",
    body: "Owners manage the business, mowers get a simple daily route, admins keep it all healthy.",
  },
  {
    icon: Leaf,
    title: "Built for lawn care only",
    body: "No generic field-service bloat — every screen is made for mowing companies.",
  },
];

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="font-display text-xl font-bold text-primary">Mowzi</span>
          <nav className="flex items-center gap-2">
            <a href="#what-we-do" className="hidden text-sm text-muted-foreground hover:text-foreground sm:block px-3">
              What we do
            </a>
            {user ? (
              <Button asChild size="sm">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/auth?mode=login">Log in</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/auth?mode=signup">Create account</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      <main>
        <section className="bg-frost">
          <div className="mx-auto max-w-6xl px-6 py-20 text-center md:py-28">
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-primary">
              Software for mowing companies
            </p>
            <h1 className="mx-auto max-w-3xl font-display text-4xl font-bold leading-tight md:text-6xl">
              Run the whole lawn business from one green dashboard
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Mowzi brings quoting, scheduling, crew routing and payments together — so owners grow,
              mowers just mow, and homeowners always know what's happening.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/auth?mode=signup">Create an account</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/auth?mode=login">Log in</Link>
              </Button>
            </div>
          </div>
        </section>

        <section id="what-we-do" className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="text-center font-display text-3xl font-bold md:text-4xl">What we do</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
            Everything a mowing company needs between "can you quote my yard?" and "you've been paid."
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="shadow-card">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-accent">
                    <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="border-t bg-accent/40">
          <div className="mx-auto flex max-w-4xl flex-col items-center px-6 py-16 text-center">
            <h2 className="font-display text-2xl font-bold md:text-3xl">Ready to get mowing?</h2>
            <p className="mt-3 text-muted-foreground">
              Create your account in under a minute. Owners, mowers and homeowners all start here.
            </p>
            <Button asChild size="lg" className="mt-8">
              <Link to="/auth?mode=signup">Get started</Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t">
        <div className="mx-auto max-w-6xl px-6 py-8 text-sm text-muted-foreground">
          © {new Date().getFullYear()} Mowzi. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
